import { useCallback, useEffect, useState } from 'react';
import {
  createNewBusinessUnitSettings,
  IClientSettings,
  getAllBusinessUnitSettings,
  updateBusinessUnitSettings,
  getWorkspaceBUUserCount,
  getBusinessUnitsByIds,
  getCurrentUserBusinessUnitsCount,
} from 'wisestep-api';

import { useWorkspaceContext } from '../../../context';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { clientSettingsDefaultPayload } from '../helper/ClientSettingsHelper';

import AddBusinessUnitDialog from './AddBusinessUnitDialog';
import BusinessUnitLayoutNew from './BusinessUnitLayoutNew';

interface IBusinessUnitSettings {
  title?: string;
  loadCurrentUserData?: boolean;
}

const BusinessUnitSettings = ({ title, loadCurrentUserData = false }: IBusinessUnitSettings) => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [businessUnits, setBusinessUnits] = useState<IClientSettings[]>([]);
  const [bussinesCount, setbussinesCount] = useState<Map<string, number>>();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [loader, setLoader] = useState<boolean>(false);
  const [businessUnit, setBusinessUnit] = useState<IClientSettings>(clientSettingsDefaultPayload);
  const [onEdit, setOnEdit] = useState(-1);

  const workspaceContext = useWorkspaceContext();
  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_CLIENT_SETTINGS,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  const fetchData = useCallback(async () => {
    setLoader(true);
    try {
      if (loadCurrentUserData) {
        const countsResponse = await getCurrentUserBusinessUnitsCount();
        const counts = countsResponse.data;
        const businessUnitsResponse = await getBusinessUnitsByIds(
          counts?.map((item) => item.businessunitid)
        );
        const keyValueFormat = counts.reduce((acc, curr) => {
          acc[curr.businessunitid] = curr.usercount;
          return acc;
        }, {} as { [key: string]: number });

        setbussinesCount(keyValueFormat);
        setBusinessUnits(businessUnitsResponse.data);
      } else {
        const response = await Promise.allSettled([
          getAllBusinessUnitSettings(),
          getWorkspaceBUUserCount(),
        ]);
        setbussinesCount(response[1].status === 'fulfilled' ? response[1].value.data : {});
        setBusinessUnits(response[0].status === 'fulfilled' ? response[0].value.data : []);
      }
    } catch (error) {
      console.error('Error fetching business units:', error);
    } finally {
      setLoader(false);
    }
  }, [loadCurrentUserData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onAddBusinessUnit = async (data) => {
    if (!manageAccess) {
      setNotification(warning('You do not have enough permissions to perform this action'));
      return;
    }
    const payload = { ...clientSettingsDefaultPayload };
    payload.name = data.name;
    payload.description = data.description ?? '';
    try {
      if (payload.name) {
        await createNewBusinessUnitSettings(payload);
        setNotification(success(`${data.name} has been added successfully !!`));
        setOpenDialog(false);
        fetchData();
      } else setNotification(failed('Please input the name again !!'));
    } catch (error: any) {
      if (error.response.status === 400)
        setNotification(warning(`Oops , ${data.name} already exists!!`));
      else setNotification(failed('Something went wrong !!'));
    }
  };

  const onUpdate = async (data: IClientSettings) => {
    const businessUnitData: IClientSettings = { ...businessUnit };
    businessUnitData.description = data.description;
    businessUnitData.name = data.name;
    if (data.id !== undefined) {
      businessUnitData.id = data.id;
      await updateBusinessUnitSettings(businessUnitData);
    }
    setNotification(success('Business Unit has been updated successfully !!'));
    setOpenDialog(false);
    fetchData();
  };

  const getBusinessUnit = (index: number) => {
    const tempPayload = businessUnits[index];
    setBusinessUnit(tempPayload);
    setOpenDialog(true);
    setOnEdit(1);
  };

  return (
    <>
      <BusinessUnitLayoutNew
        title={title === 'Teams Business Units' ? 'Teams Business Units' : 'Business Units'}
        backUrl={
          title === 'Teams Business Units'
            ? '/workspace-users-and-teams'
            : '/app-settings/teams-and-users'
        }
        headerList={['Unit Name', 'Visibility']}
        businessUnits={businessUnits}
        bussinesCount={bussinesCount}
        addButtonText="Business Unit"
        handleAddButton={() => {
          setOpenDialog(true);
          setBusinessUnit(clientSettingsDefaultPayload);
          setOnEdit(0);
        }}
        loader={loader}
        getBusinessUnit={getBusinessUnit}
      />
      {openDialog && (
        <AddBusinessUnitDialog
          openDialog={openDialog}
          closeDialog={() => setOpenDialog(false)}
          onInput={(data) => onAddBusinessUnit(data)}
          businessUnit={businessUnit}
          dataCallback={(data: IClientSettings) => onUpdate(data)}
          onEdit={onEdit}
        />
      )}
    </>
  );
};

export default BusinessUnitSettings;
